import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { StatusPill } from "@/components/StatusBanner";
import { PaginationBar } from "@/components/PaginationBar";
import { WidgetError } from "@/components/WidgetError";
import { fmtDateShort } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";

/** History = completed orders (with search, filters, paginated). Owner/Admin only. */
export default function History() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 300);
  const [method, setMethod] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => { setPage(1); }, [debouncedQ, method, dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = { page, limit: 20, status: "completed" };
    if (debouncedQ) params.q = debouncedQ;
    if (method) params.completion_method = method;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/orders", { params })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, debouncedQ, method, dateFrom, dateTo, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  return (
    <div>
      <div className="mb-6">
        <div className="label-overline">Operations</div>
        <h1 className="text-3xl font-black tracking-tight mt-1 text-slate-900">History</h1>
        <p className="text-slate-600 mt-1 text-sm">Completed operational orders. No financial data.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <Input data-testid="hist-q" placeholder="Search order # or reg…"
               className="rounded h-10 font-mono"
               value={q} onChange={(e) => setQ(e.target.value)} />
        <select data-testid="hist-method"
                className="h-10 border border-slate-300 rounded px-2 font-mono"
                value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="">All payment methods</option>
          <option value="cash">cash</option>
          <option value="card">card</option>
          <option value="order">order</option>
        </select>
        <Input type="date" data-testid="hist-from"
               className="rounded h-10 font-mono"
               value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Input type="date" data-testid="hist-to"
               className="rounded h-10 font-mono"
               value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        {error ? (
          <div className="p-4">
            <WidgetError
              error={error}
              onRetry={retry}
              title="Could not load history"
              testId="history-error"
            />
          </div>
        ) : (
        <table className="tbl w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="label-overline text-slate-600">
              <th className="text-left px-4 py-3">Order</th>
              <th className="text-left px-4 py-3">Registration</th>
              <th className="text-left px-4 py-3">Service</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Method</th>
              <th className="text-left px-4 py-3">Driver Order #</th>
              <th className="text-left px-4 py-3">Completed</th>
              <th className="text-left px-4 py-3">Finance</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-500">No history matches.</td></tr>
            )}
            {data.items.map((o) => {
              const isReversed = o.finance_status === "reversed";
              return (
              <tr key={o.id} className={"border-b border-slate-100 " + (isReversed ? "bg-amber-50/40" : "")}
                  data-testid={`hist-row-${o.order_no}`}>
                <td className="px-4 py-3 font-mono">{o.order_no}</td>
                <td className="px-4 py-3 font-mono font-bold text-blue-700">{o.registration_no}</td>
                <td className="px-4 py-3">{o.service_type_snapshot?.label}</td>
                <td className="px-4 py-3">
                  {o.customer_snapshot?.display_name || <span className="text-slate-500">Walk-in</span>}
                </td>
                <td className="px-4 py-3 font-mono">{o.completion_method || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{o.driver_order_number || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{fmtDateShort(o.completed_at)}</td>
                <td className="px-4 py-3">
                  {isReversed && (
                    <StatusPill status="reversed" testId={`hist-reversed-${o.order_no}`}/>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Link to={`/app/orders/${o.id}`} data-testid={`hist-open-${o.order_no}`}
                        className="text-blue-700 hover:underline font-semibold">Open →</Link>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        )}
        <PaginationBar page={data.page || 1} pages={data.pages || 1} onChange={setPage} />
      </div>
    </div>
  );
}
